import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { HomeStep } from '@/modules/home/components/HomeStep/HomeStep'
import { LoginRoutePathsEnum } from '@/modules/login/routes/loginRouteConfig.types'
import { RegisterRoutePathsEnum } from '@/modules/register/routes/registerRouteConfig.types'

import { useUserStore } from '@/store/user.store'

import { Button } from '@/components/Button/Button'

import { getUserDefaultPath } from '@/helpers/getUserDefaultPath.helper'

type WelcomeSteps = 'first' | 'second' | 'final'

const WELCOME_SEEN_KEY = 'welcomeSeen'

export function Component() {
  const [currentStep, setCurrentStep] = useState<WelcomeSteps>('first')
  const user = useUserStore.use.user()
  const currentRole = useUserStore.use.currentRole()

  const navigate = useNavigate()

  const hasSeenWelcome = localStorage.getItem(WELCOME_SEEN_KEY)

  const nextStep = useCallback(() => {
    switch (currentStep) {
      case 'first':
        setCurrentStep('second')
        break
      case 'second':
        setCurrentStep('final')
        break
      case 'final':
      default:
        navigate(RegisterRoutePathsEnum.REGISTER)
        break
    }
  }, [currentStep, navigate])

  useEffect(() => {
    if (currentStep === 'final') {
      localStorage.setItem(WELCOME_SEEN_KEY, 'true')
    }
  }, [currentStep])

  if (user) {
    return <Navigate to={getUserDefaultPath(user, currentRole)} />
  }

  if (hasSeenWelcome) {
    return <Navigate to={LoginRoutePathsEnum.LOGIN} />
  }

  return (
    <>
      {currentStep === 'first' && (
        <HomeStep
          button={
            <Button
              mt="xl"
              onClick={nextStep}
              size="compact-md"
              style={{ alignSelf: 'center' }}
            >
              Next
            </Button>
          }
          imgSrc="welcome_image_step_1.png"
          text="We'll help you achieve your financial and life goals through
          personalized guidance."
          title="Welcome to Rolan Wealth!"
        />
      )}

      {currentStep === 'second' && (
        <HomeStep
          button={
            <Button
              mt="xl"
              onClick={nextStep}
              size="compact-md"
              style={{ alignSelf: 'center' }}
            >
              Next
            </Button>
          }
          imgSrc="welcome_image_step_2.png"
          text="You'll get a sense of where your money is, what areas you
              want to work on, and track your improvements. Still have
              questions? Ask your financial planner anything, any time!"
          title="Rolan Wealth gives you a holistic view of all your finances and
              goals in one place."
        />
      )}

      {currentStep === 'final' && (
        <HomeStep
          button={
            <Button
              mt="xl"
              onClick={nextStep}
              size="compact-md"
              style={{ alignSelf: 'center' }}
            >
              Let&apos;s get started
            </Button>
          }
          imgSrc="welcome_image_step_3.png"
          isFinal
          title="To help you achieve your dreams, we first need to learn more about
              you, including your goals, income, expenses, and more."
        />
      )}
    </>
  )
}

Component.displayName = 'Home'
